import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
} from '@mui/material';
import {
  Edit as EditIcon,
  Visibility as ViewIcon,
  Send as SendIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from '../api/axios';

const EstimateTile = ({ estimate, onUpdate }) => {
  const navigate = useNavigate();
  const { effectiveRole } = useAuth();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const getStatusColor = () => {
    switch (estimate.status) {
      case 'estimate': return 'warning';
      case 'pending_approval': return 'info';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const getStatusLabel = () => {
    switch (estimate.status) {
      case 'estimate': return 'Draft';
      case 'pending_approval': return 'Pending Approval';
      case 'rejected': return 'Rejected';
      default: return estimate.status;
    }
  };

  const getBorderColor = () => {
    switch (estimate.status) {
      case 'estimate': return '#ed6c02'; // warning.main
      case 'pending_approval': return '#0288d1'; // info.main
      case 'rejected': return '#d32f2f'; // error.main
      default: return '#0d47a1';
    }
  };

  const handleClick = () => {
    // Homeowners review estimates, contractors edit them
    if (effectiveRole === 'homeowner' && estimate.status === 'pending_approval') {
      navigate(`/estimate-review/${estimate.id}`);
    } else {
      navigate(`/estimate/${estimate.id}`);
    }
  };

  const handleAction = (e) => {
    e.stopPropagation();
    if (effectiveRole === 'homeowner' && estimate.status === 'pending_approval') {
      navigate(`/estimate-review/${estimate.id}`);
    } else {
      navigate(`/estimate/${estimate.id}`);
    }
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await axios.delete(`/api/estimates/${estimate.id}`);
      setDeleteDialogOpen(false);
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error deleting estimate:', error);
      alert(error.response?.data?.error || 'Failed to delete estimate');
      setDeleteDialogOpen(false);
    }
  };

  const handleDeleteCancel = (e) => {
    e.stopPropagation();
    setDeleteDialogOpen(false);
  };

  // Admin can nuke any estimate/project regardless of status (cleanup
  // power-user role). Contractors are kept to the safer gate: only
  // drafts, pending-approval, or rejected — i.e. estimates that haven't
  // turned into real projects with disbursements.
  const canDelete =
    effectiveRole === 'admin' ||
    (effectiveRole === 'contractor' && ['estimate', 'pending_approval', 'rejected'].includes(estimate.status));

  return (
    <Card
      sx={{
        transition: 'all 0.3s ease',
        cursor: 'pointer',
        borderLeft: `6px solid ${getBorderColor()}`,
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 4,
        },
      }}
      onClick={handleClick}
    >
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
          <Box flex={1}>
            <Typography variant="h6" gutterBottom>
              {estimate.name}
            </Typography>
            {effectiveRole === 'contractor' ? (
              <Typography variant="body2" color="text.secondary">
                For: {estimate.homeowner_first_name} {estimate.homeowner_last_name}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                From: {estimate.contractor_first_name} {estimate.contractor_last_name}
              </Typography>
            )}
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Chip
              label={getStatusLabel()}
              color={getStatusColor()}
              size="small"
            />
            {canDelete && (
              <IconButton
                onClick={handleDeleteClick}
                color="error"
                size="small"
                sx={{ ml: 1 }}
                aria-label="Delete estimate"
              >
                <DeleteIcon />
              </IconButton>
            )}
          </Box>
        </Box>

        {estimate.status === 'rejected' && estimate.rejection_reason && (
          <Box
            sx={{
              backgroundColor: 'rgba(211, 47, 47, 0.08)',
              border: '1px solid rgba(211, 47, 47, 0.3)',
              borderRadius: 1,
              p: 1.5,
              mb: 2,
            }}
          >
            <Typography variant="caption" color="error" fontWeight="bold">
              Rejection Reason:
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {estimate.rejection_reason}
            </Typography>
          </Box>
        )}

        <Box mt={2}>
          <Typography variant="caption" color="text.secondary" display="block">
            Created: {new Date(estimate.created_at).toLocaleDateString()}
          </Typography>
          {estimate.submitted_at && (
            <Typography variant="caption" color="text.secondary" display="block">
              Submitted: {new Date(estimate.submitted_at).toLocaleDateString()}
            </Typography>
          )}
        </Box>

        <Box mt={2}>
          {effectiveRole === 'contractor' ? (
            <Button
              variant="outlined"
              size="small"
              startIcon={estimate.status === 'estimate' || estimate.status === 'rejected' ? <EditIcon /> : <ViewIcon />}
              onClick={handleAction}
              fullWidth
            >
              {estimate.status === 'estimate' ? 'Continue Editing' :
               estimate.status === 'rejected' ? 'Edit & Resubmit' :
               'View Estimate'}
            </Button>
          ) : (
            <Button
              variant={estimate.status === 'pending_approval' ? 'contained' : 'outlined'}
              size="small"
              startIcon={estimate.status === 'pending_approval' ? <SendIcon /> : <ViewIcon />}
              onClick={handleAction}
              fullWidth
              color={estimate.status === 'pending_approval' ? 'primary' : 'inherit'}
            >
              {estimate.status === 'pending_approval' ? 'Review & Approve' : 'View Estimate'}
            </Button>
          )}
        </Box>
      </CardContent>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        onClick={(e) => e.stopPropagation()}
      >
        <DialogTitle>Delete Estimate</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the estimate "{estimate.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default React.memo(EstimateTile);
